import { liveReload } from '@shared/services/realtime.service';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { Component, OnInit, inject, signal, ViewChild } from '@angular/core';

import { Router } from '@angular/router';
// Material
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
// Services & Models
import { ModulesService } from '@shared/services/modules.service';
import { ModuleModel } from '@shared/models/entities/module.model';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
//Utils
import { sortAlphabetically } from '@shared/utils/sort.utils';
import { ModuleFormDialogComponent } from './module-form-dialog/module-form-dialog.component';


import { ListSelection, ListView } from '@app/shared/utils/list-view';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectionBarComponent } from '@app/shared/components/selection-bar/selection-bar.component';
import { SimpleExcelService } from '@shared/services/simple-excel.service';
@Component({
  selector: 'app-modules',
  standalone: true,
  imports: [ MatButtonToggleModule, MatCheckboxModule, SelectionBarComponent,PageLoaderComponent, SearchPillComponent, FilterChipsComponent, PageTitleComponent, 
    MatTableModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatDividerModule,
    MatDialogModule
],
  templateUrl: './modules.component.html',
  styleUrl: './modules.component.scss',
})
export class ModulesComponent implements OnInit {
  /** Vista tabla / tarjetas y selección con checkbox (misma que en catálogos). */
  readonly listView = new ListView('modules', 'table');
  readonly sel = new ListSelection<any>(() => (this.dataSource?.filteredData ?? []) as any[]);
  private readonly excelSvc = inject(SimpleExcelService);

  /** Exporta a Excel la selección. */
  async exportExcel(rows: any[]): Promise<void> {
    if (!rows?.length) return;
    await this.excelSvc.simpleXLSX({
      fileName: 'Módulos',
      sheets: [{
        sheetName: 'Módulos'.slice(0, 31), header: [], lineBreak: 0,
        columns: [
          { columnKey: 'name', columnName: 'Nombre' },
          { columnKey: 'label', columnName: 'Etiqueta' },
          { columnKey: 'route', columnName: 'Ruta' },
          { columnKey: 'icon', columnName: 'Ícono' },
          { columnKey: 'description', columnName: 'Descripción' },
          { columnKey: '_active', columnName: 'Estado' },
        ],
        data: rows.map((r: any) => ({ ...r, _active: r.active ? 'Activo' : 'Inactivo' })),
      }],
    } as any);
  }

  private modulesSvcTitle = inject(ModulesService);
  /** Título del módulo = etiqueta visible del catálogo de Módulos. */
  title = this.modulesSvcTitle.moduleTitle('/modules', 'Módulos del Sistema');


  private readonly modulesService       = inject(ModulesService);
  private readonly customMessageService = inject(CustomMessageService);
  private readonly router               = inject(Router);
  private readonly dialog               = inject(MatDialog);

  // ─── Estado ────────────────────────────────────────────────────────────────
  loading    = this.modulesService.loading;
  searchText = signal('');

  // ─── Tabla ─────────────────────────────────────────────────────────────────
  columns: string[] = ['select', 'icon', 'name', 'label', 'route', 'active', 'actions'];
  dataSource = new MatTableDataSource<ModuleModel>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['modules'], () => this.pageLoad.ready && this.pageLoad.refresh(this.loadModules()));

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    await this.loadModules();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Alta / edición: ahora en diálogo (título y acciones fijos, contenido
  // scrolleable) en lugar del panel inline que obligaba a scrollear la página.
  // ─────────────────────────────────────────────────────────────────────────────

  openCreate(): void {
    this.openFormDialog();
  }

  openEdit(module: ModuleModel): void {
    this.openFormDialog(module);
  }

  private openFormDialog(module?: ModuleModel): void {
    const ref = this.dialog.open(ModuleFormDialogComponent, {
      data: { module },
      width: '640px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: 'first-tabbable',
    });
    ref.afterClosed().subscribe(saved => {
      if (saved) this.loadModules();
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // CRUD
  // ─────────────────────────────────────────────────────────────────────────────

  async loadModules(): Promise<void> {
    try {
      await this.modulesService.search({
        searchType: 'default',
        condition: { deleted: false },
      });
      this.dataSource.data = sortAlphabetically(this.modulesService.listData(), 'label');
      this.dataSource.paginator = this.paginator;
      this.applyFilter(this.searchText());
    } catch {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudieron cargar los módulos.',
        dismissBtn: true,
      });
    }
  }

  async toggleActive(module: ModuleModel): Promise<void> {
    try {
      await this.modulesService.update({
        id:            module.id,
        active:        !module.active,
        operationType: 'single' as any,
      });
      await this.loadModules();
    } catch {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudo cambiar el estado.',
        dismissBtn: true,
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Búsqueda / filtro
  // ─────────────────────────────────────────────────────────────────────────────

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchText.set(value);
    this.applyFilter(value);
  }

  private applyFilter(value: string): void {
    this.dataSource.filter = value.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  // ─── Helpers template ───────────────────────────────────────────────────────

  navigateTo(route: string): void {
    this.router.navigate([route]);
  }
}